/**
 * Backend selection: the real API when it answers GET /api/health quickly,
 * otherwise the offline demo backend (clearly labelled in the UI via
 * `backend.kind === 'offline-demo'`).
 *
 *   const backend = await createBackend();
 *   const profile = await backend.ensurePlayer();
 */
import type { BackendClient } from '../../contracts.ts';
import { ApiBackend, probeServer, type ApiBackendOptions } from './ApiBackend.ts';
import { OfflineDemoBackend, type OfflineDemoBackendOptions } from './OfflineDemoBackend.ts';

export { ApiBackend, CLIENT_VERSION, probeServer, type ApiBackendOptions, type FetchLike } from './ApiBackend.ts';
export { OfflineDemoBackend, type OfflineDemoBackendOptions } from './OfflineDemoBackend.ts';
export { BackendError, type BackendErrorKind } from './errors.ts';
export * from './api-types.ts';

/** How long the startup health probe may take before we go offline. */
export const HEALTH_PROBE_TIMEOUT_MS = 1500;

export interface CreateBackendOptions extends ApiBackendOptions, OfflineDemoBackendOptions {
  probeTimeoutMs?: number;
}

/**
 * Probe the API (1.5 s timeout) and return the matching backend. Never rejects:
 * an unreachable, slow or unhealthy server selects OfflineDemoBackend.
 */
export async function createBackend(options: CreateBackendOptions = {}): Promise<BackendClient> {
  const health = await probeServer({
    baseUrl: options.baseUrl,
    fetch: options.fetch,
    timeoutMs: options.probeTimeoutMs ?? HEALTH_PROBE_TIMEOUT_MS,
  });
  return health ? new ApiBackend(options) : new OfflineDemoBackend(options);
}
